/**
 * Error code → HTTP status map — 04 §9.3.
 *
 * This object is the single source of truth. The API resolves the status of an
 * `AppException` from it; both frontends map the code to an Arabic string.
 * Renaming a member breaks the i18n maps, so 08 §2.6 snapshots it.
 */
export const ErrorCode = {
    // scan
    TOKEN_INVALID: 400,
    TOKEN_EXPIRED: 400,
    SECTION_NOT_OPEN: 409,
    OUT_OF_RANGE: 403,
    LOW_ACCURACY: 422,
    DEVICE_ALREADY_USED: 409,
    DEVICE_PENDING_APPROVAL: 403,
    ALREADY_RECORDED: 409,
    STUDENT_NOT_ENROLLED: 403,
    RATE_LIMITED: 429,
    DEVICE_LIMIT_DAILY: 429, // 02 D13
    /**
     * The last-4 confirmation of a roster claim did not match the student the
     * caller picked (M2, `Section.identityCheck`). Distinct from
     * `RESET_CODE_INVALID`: nothing here is a credential and nothing is
     * rate-limited as one — it is a typo guard on an unauthenticated claim.
     */
    IDENTITY_CONFIRM_INVALID: 422,
    // auth
    AUTH_REQUIRED: 401,
    AUTH_TOKEN_EXPIRED: 401,
    AUTH_TOKEN_INVALID: 401,
    AUTH_INVALID_CREDENTIALS: 401,
    AUTH_REFRESH_INVALID: 401,
    AUTH_REFRESH_REUSED: 401,
    FORBIDDEN: 403,
    PASSWORD_CHANGE_REQUIRED: 403,
    STUDENT_SUSPENDED: 403,
    PASSWORD_TOO_WEAK: 422,
    PASSWORD_SAME_AS_OLD: 422,
    RESET_CODE_INVALID: 401,
    // generic
    VALIDATION_ERROR: 422,
    NOT_FOUND: 404,
    CONFLICT: 409,
    INTERNAL: 500,
    // domain
    USER_EMAIL_TAKEN: 409,
    USER_HAS_COURSES: 409,
    COHORT_NAME_TAKEN: 409,
    COHORT_NOT_EMPTY: 409,
    COURSE_CODE_TAKEN: 409,
    COURSE_HAS_SECTIONS: 409,
    COURSE_HAS_OPEN_SECTION: 409,
    STUDENT_UNIVERSITY_ID_TAKEN: 409,
    STUDENT_HAS_ATTENDANCE: 409,
    IMPORT_INVALID_FILE: 422,
    IMPORT_ROW_ERRORS: 422,
    IMPORT_EXPIRED: 404,
    SECTION_NOT_DRAFT: 409,
    SECTION_ALREADY_OPEN: 409,
    SECTION_CLOSED: 409,
    SECTION_NOT_CLOSED: 409,
    SECTION_LOCATION_MISSING: 422,
    /**
     * `isOnline` and `locationRequired` were both asked for. An online section
     * has no hall to stand in, so a geofence on it could only ever reject every
     * student. Mirrors the DB CHECK `sections_online_no_location_required`.
     */
    SECTION_ONLINE_NO_LOCATION: 422,
    SECTION_EXTEND_IN_PAST: 422,
    SECTION_DRAFT: 409,
    SECTION_LOCATION_OFF_CAMPUS: 422,
    SECTION_REOPEN_WINDOW_PASSED: 409,
    SECTION_HEADCOUNT_WINDOW_PASSED: 409,
    HEADCOUNT_REQUIRED: 422,
    CHECKPOINT_ALREADY_OPEN: 409,
    SPOTCHECK_EXPIRED: 409,
    ATTENDANCE_SCAN_IMMUTABLE: 409,
    MANUAL_VERIFY_REQUIRED: 422,
    DEVICE_LIMIT_REACHED: 409,
    DEVICE_VERIFY_REQUIRED: 422,
    SHARE_ALLOWANCE_EXISTS: 409,
    HALL_IN_USE: 409,
    GRADING_STEPS_REQUIRED: 422,
    ACTIVITY_MARKS_EXCEED_MAX: 422, // 09 §2.7/§2.8 activity ledger ceiling
    EXPORT_EXPIRED: 404,
    SETTINGS_POLYGON_INVALID: 422, // 04 §4.8a campus polygon validation
};
/**
 * Every code name, in declaration order — a non-empty tuple so it can be fed
 * straight to `z.enum`. Useful for snapshots and i18n coverage audits.
 */
export const ERROR_CODES = Object.keys(ErrorCode);
/** Narrowing type guard: is `value` a member of {@link ErrorCode}? */
export function isErrorCode(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(ErrorCode, value);
}
/**
 * HTTP status for a code. Unknown codes fall back to 500 so a client that
 * receives a code from a newer server still behaves sanely.
 */
export function httpStatusFor(code) {
    return isErrorCode(code) ? ErrorCode[code] : ErrorCode.INTERNAL;
}
/** Convenience constructor so producers cannot forget a field. */
export function errorEnvelope(code, message, details, requestId) {
    const envelope = { code, message };
    if (details !== undefined)
        envelope.details = details;
    if (requestId !== undefined)
        envelope.requestId = requestId;
    return envelope;
}
//# sourceMappingURL=error-codes.js.map